import { useLayoutEffect, useRef, useState } from "react";

/**
 * Compute a column count for a wrapping grid that keeps rows balanced.
 *
 * A plain CSS `repeat(auto-fit, minmax(...))` greedily packs as many items as
 * fit on the first row and dumps the remainder on the last row (e.g. 8 items ->
 * 6 + 2). This hook instead measures the container, works out how many columns
 * physically fit, then trims the column count so the items spread as evenly as
 * possible across the rows they already need (e.g. 8 items -> 4 + 4).
 *
 * @param {number} itemCount     Number of items being laid out.
 * @param {object} [options]
 * @param {number} [options.minItemWidth=85]  Minimum width (px) of one item.
 * @param {number} [options.gap=8]            Grid gap (px) between items.
 * @returns {[React.RefObject, number]} `[containerRef, columns]`.
 */
export default function useBalancedColumns(
  itemCount,
  { minItemWidth = 85, gap = 8 } = {},
) {
  const containerRef = useRef(null);
  const [columns, setColumns] = useState(Math.max(1, itemCount || 1));

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const recompute = () => {
      const width = node.clientWidth;
      const count = Math.max(0, itemCount || 0);
      if (!width || count <= 1) {
        setColumns(Math.max(1, count));
        return;
      }
      // How many columns physically fit at the minimum item width.
      const maxFit = Math.max(1, Math.floor((width + gap) / (minItemWidth + gap)));
      const cappedFit = Math.min(maxFit, count);
      // Rows we'd need at full packing, then spread evenly across those rows.
      const rows = Math.ceil(count / cappedFit);
      const balanced = Math.max(1, Math.ceil(count / rows));
      setColumns(balanced);
    };

    recompute();

    const observer = new ResizeObserver(recompute);
    observer.observe(node);
    return () => observer.disconnect();
  }, [itemCount, minItemWidth, gap]);

  return [containerRef, columns];
}
